import { createContext, useCallback, useContext, useMemo, useState } from "react";
import useTourCompletion from "../hooks/useTourCompletion";

const TourContext = createContext(null);

// Lives above the routes so completion state survives the remount that
// AddCaseForm's location key forces on every navigation
export const TourProvider = ({ children }) => {
   const [activeTourId, setActiveTourId] = useState(null);
   const [requestedTourId, setRequestedTourId] = useState(null);
   const { isTourPending, isRefresherDue, completeTour, dismissTour, markRefresherOffered } =
      useTourCompletion();

   // Route-scoped tours announce themselves so shell UI can offer a replay
   const registerTour = useCallback((tourId) => {
      setActiveTourId(tourId);
      return () => setActiveTourId((current) => (current === tourId ? null : current));
   }, []);

   const requestTour = useCallback((tourId) => setRequestedTourId(tourId), []);

   const clearTourRequest = useCallback(() => setRequestedTourId(null), []);

   const value = useMemo(
      () => ({
         activeTourId,
         registerTour,
         requestedTourId,
         requestTour,
         clearTourRequest,
         isTourPending,
         isRefresherDue,
         completeTour,
         dismissTour,
         markRefresherOffered,
      }),
      [
         activeTourId,
         registerTour,
         requestedTourId,
         requestTour,
         clearTourRequest,
         isTourPending,
         isRefresherDue,
         completeTour,
         dismissTour,
         markRefresherOffered,
      ]
   );

   return <TourContext.Provider value={value}>{children}</TourContext.Provider>;
};

export const useTourContext = () => {
   const context = useContext(TourContext);

   if (!context) {
      throw new Error("useTourContext must be used within TourProvider");
   }

   return context;
};

// The partner embed renders the case form outside the layout shell, so callers
// shared with that route have to tolerate a missing provider
export const useOptionalTourContext = () => useContext(TourContext);

export default TourContext;
